import * as _ from 'lodash';
import {PAdbContext} from "../PA.Core/PAdbContext";
import {WB_FinishedProductRow, WB_ProductBOMRow} from "../PA.Core/PAdbSchemaRows";
import * as AsyncLock from 'async-lock';
import {esWB_Finished_Product, esWB_Product_BOM} from "../PA.Core/PAdbSchema";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {ILease} from "../PA.Utils/PA.UtilsIF";
import {GenericLease} from '../PA.Utils/GenericLease';
import {LeaseRegistry} from "../PA.Utils/LeaseRegistry";
import {IDateRange} from "../PA.Core/PA.Core.IF.Basic";

export interface IBOMComponent {
    Component: number | string
    ComponentWbID: number
    Qty: number
    QtyBUOM: number
    UOM: string
    EffectiveDateRange?: IDateRange
}

export class ProductBOMService {

    private static _dbContext: PAdbContext;

    private static dctFinishedPrdsByBOMWbID: { [id: number]: {[wbFinishedPrdId: number] : WB_FinishedProductRow}} = {};
    private static dctBOMRowsByBOMWbID: { [id: number]: WB_ProductBOMRow[] } = {};
    private static dctComponentPrdsByBOMWbID: {[id: number] : IBOMComponent[]} = {};

    private static dctLock: any = new AsyncLock();
    private static bIsLoaded: boolean = false;
    private static _lease: ILease;

    private static readonly _maxDepth: number = 10;

    static getBOMRowsByBOMWbProdID(wbProdID: number): Promise<WB_ProductBOMRow[]> {
        return new Promise<WB_ProductBOMRow[]>((resolve, error) => {

            ProductBOMService.dctLock.acquire(wbProdID.toString(),
                (done: any) => {

                    ProductBOMService.checkLease()
                        .then((ignore) => {
                            if (wbProdID in ProductBOMService.dctBOMRowsByBOMWbID) {
                                done(null, ProductBOMService.dctBOMRowsByBOMWbID[wbProdID]);
                                resolve(ProductBOMService.dctBOMRowsByBOMWbID[wbProdID]);
                                return;
                            } else if (!(wbProdID in ProductBOMService.dctBOMRowsByBOMWbID) && ProductBOMService.bIsLoaded) {
                                done(null, ProductBOMService.dctBOMRowsByBOMWbID[wbProdID]);
                                resolve([]);
                                return;
                            }

                            return ProductBOMService.loadFromDB()
                        })
                        .then((ignore) => {
                            done(null, ProductBOMService.dctBOMRowsByBOMWbID[wbProdID]);
                            resolve(ProductBOMService.dctBOMRowsByBOMWbID[wbProdID]);
                            return;
                        })
                        .catch((err2) => {
                            error(err2);
                        })
                        .catch((err) => {
                            error(err);
                        })
                }
            );
        });
    }

    static getBomRowsByBOMWbProdIDDateRange(wbProductID: number, effectiveStartDt: Date, effectiveEndDate?: Date): Promise<WB_ProductBOMRow[]> {

        return new Promise<WB_ProductBOMRow[]>((resolve, error) => {

            ProductBOMService.getBOMRowsByBOMWbProdID(wbProductID)
                .then((results) => {
                    if (_.isEmpty(results)) {
                        resolve([]);
                        return;
                    } else {
                        if (_.isUndefined(effectiveEndDate)) {
                            effectiveEndDate = DateUtils.getMaxDate();
                        }
                        let validRows = _.filter(results, (aResult) => {
                            return DateUtils.RangeDatesOverlap(aResult.EffectiveStartDt,aResult.EffectiveEndDt,
                                effectiveStartDt, effectiveEndDate);
                        });
                        resolve(validRows);
                        return;
                    }
                })
                .catch((err) => {
                    error(err);
                })
        })
    }

    static findBOMComponents(wbProductID: number): Promise<IBOMComponent[]> {

        return new Promise<IBOMComponent[]>((resolve, error) => {
            let dctResults: { [id: number]: IBOMComponent } = {};
            let retVal: IBOMComponent[];
            ProductBOMService.checkLease()
                             .then((ignore) => {

                                 if (wbProductID in ProductBOMService.dctComponentPrdsByBOMWbID) {
                                     retVal = ProductBOMService.dctComponentPrdsByBOMWbID[wbProductID];
                                     return;
                                 }
                                 return ProductBOMService.findBOMComponentRecursive(wbProductID, dctResults, 1, 1, "",0);
                             })
                             .then((ignore) => {
                                 if (_.isUndefined(retVal)) { //if retVal is undefined, then the value wasn't cached, so use the recursion results
                                     _.forEach(dctResults, (aBomComponent, aBomCompWbPrdId: any) => {
                                         let finishedPrd = (ProductBOMService.dctFinishedPrdsByBOMWbID[wbProductID] || {})[aBomCompWbPrdId];
                                         if (!finishedPrd) {
                                             throw new Error(`Unable to find finished product with WB_PROD_ID ${wbProductID} for BOM with WB_PROD_ID ${wbProductID}`);
                                         }
                                         aBomComponent.Qty *= finishedPrd.cif.CASE_PACK_SIZE;
                                     })
                                     ProductBOMService.dctComponentPrdsByBOMWbID[wbProductID] = _.values(dctResults);
                                     retVal = ProductBOMService.dctComponentPrdsByBOMWbID[wbProductID];
                                 }
                             })
                             .then((ignore) => {
                                 resolve(retVal);
                             })
                             .catch((err) => {
                                 error(err);
                             });
        });

    }

    private static async findBOMComponentRecursive(wbProductID: number, dctResults: { [p: number]: IBOMComponent }, scaleQty: number, buomQty: number, uom: string, depth: number) {

        let prdBOM = await ProductBOMService.getBOMRowsByBOMWbProdID(wbProductID);

        if ((_.isEmpty(prdBOM) && depth == 0) || depth > ProductBOMService._maxDepth) {
            return; //if we're at the top level and there are no BOM rows returned, then we've come across an empty BOM return.
            // if we've gone too deep into recursion, likely have a self-referencing BOM and should break after some number of cycles
        }

        if (_.isEmpty(prdBOM) && depth > 0) {
            //if product already exists, add the component qty to what we've calculated so far in the recursion
            if (wbProductID in dctResults) {
                scaleQty += dctResults[wbProductID].Qty;
                buomQty += dctResults[wbProductID].QtyBUOM
            }

            dctResults[wbProductID] = {
                Component: wbProductID,
                ComponentWbID: wbProductID,
                Qty: scaleQty,
                QtyBUOM: buomQty,
                UOM: uom
            };
            return;
        }

        for (let aPrdBOM of prdBOM) {
            let newDepth = depth + 1;
            await ProductBOMService.findBOMComponentRecursive(aPrdBOM.cif.WB_PROD_ID_COMP, dctResults, scaleQty * aPrdBOM.cif.COMPONENT_QTY, buomQty * aPrdBOM.cif.COMPONENT_QTY_BUOM, aPrdBOM.cif.COMPONENT_UOM, newDepth)
        }
    }

    private static loadFromDB(): Promise<void> {
        return new Promise<void>((resolve, error) => {

            ProductBOMService.bIsLoaded = false;

            if (_.isUndefined(ProductBOMService._dbContext)) {
                ProductBOMService.instantiateDbContext();
            }

            let dctFinishedPrdsToBOMs: {[wbFinishedProdID: number]: number[]} = {}; //map of finished products to BOMs to populate BOM->Finished Prd dct after getting FnsPrds from DB call

            ProductBOMService._dbContext.findAll(esWB_Product_BOM, {where: {DELETED_IND: {neq: '1'}}})
                .then((bomRecords) => {
                    let dctPrdWbIDs: {[wbProdID: number]: number} = {};
                    if (_.isArray(bomRecords)) {

                        _.forEach(bomRecords, (aBomRecord: WB_ProductBOMRow) => {

                            //instantiate dictionary collection mapping BOMs to their finished products
                            if (!(aBomRecord.cif.WB_PROD_ID in ProductBOMService.dctFinishedPrdsByBOMWbID)) {
                                ProductBOMService.dctFinishedPrdsByBOMWbID[aBomRecord.cif.WB_PROD_ID] = {};
                            }

                            //populate dictionary of BOMs to their component BOM rows
                            if (!(aBomRecord.cif.WB_PROD_ID in ProductBOMService.dctBOMRowsByBOMWbID)) {
                                ProductBOMService.dctBOMRowsByBOMWbID[aBomRecord.cif.WB_PROD_ID] = [];
                            }
                            ProductBOMService.dctBOMRowsByBOMWbID[aBomRecord.cif.WB_PROD_ID].push(aBomRecord);

                            //keep record of finished products to which BOMs they're in. used for after getting finished prds from DB to populate BOM -> Finished Prd dictionary
                            if (!(aBomRecord.cif.WB_PROD_ID_COMP in dctFinishedPrdsToBOMs)) {
                                dctFinishedPrdsToBOMs[aBomRecord.cif.WB_PROD_ID_COMP] = [];
                            }
                            dctFinishedPrdsToBOMs[aBomRecord.cif.WB_PROD_ID_COMP].push(aBomRecord.cif.WB_PROD_ID);

                            dctPrdWbIDs[aBomRecord.cif.WB_PROD_ID_COMP] = aBomRecord.cif.WB_PROD_ID_COMP;
                        })
                    }
                    if (!_.isEmpty(dctPrdWbIDs)) {
                        return ProductBOMService._dbContext.findAll(esWB_Finished_Product, {where: {WB_PROD_ID: {in: _.values(dctPrdWbIDs)}}});
                    } else {
                        return;
                    }
                })
                .then((finishedPrds) => {
                    if (_.isArray(finishedPrds)) {
                        //populate the BOM -> Finished Products dictionary with finished product records
                        _.forEach(finishedPrds, (aFnsPrd: WB_FinishedProductRow) => {
                            if (aFnsPrd.cif.WB_PROD_ID in dctFinishedPrdsToBOMs) {
                                _.forEach(dctFinishedPrdsToBOMs[aFnsPrd.cif.WB_PROD_ID], (BOMwbProdID) => {
                                    ProductBOMService.dctFinishedPrdsByBOMWbID[BOMwbProdID][aFnsPrd.cif.WB_PROD_ID] = aFnsPrd;
                                })
                            }
                        })
                    }
                    ProductBOMService.bIsLoaded = true;
                    ProductBOMService._lease.resetLease();
                })
                .finally(() => {
                    resolve();
                })
                .catch((err) => {
                    error(err)
                });

        })
    }

    private static async checkLease(): Promise<void> {
        if (_.isUndefined(ProductBOMService._lease)) {
            ProductBOMService.createLease();
        }

        if (await ProductBOMService._lease.hasDataChanged()) {
            await ProductBOMService.reset();
        }
    }

    private static createLease() {
        ProductBOMService._lease = new GenericLease(LeaseRegistry.Lease_Type_Product_BOM);
    }

    private static async reset(): Promise<void> {

        ProductBOMService.instantiateDbContext();

        let arr: WB_ProductBOMRow[] = [];

        _.forEach(_.values(ProductBOMService.dctBOMRowsByBOMWbID), (aBomArr) => {
            arr.push.apply(arr, aBomArr);
        });

        await ProductBOMService._dbContext.purgeCache(esWB_Product_BOM, arr);

        ProductBOMService.dctFinishedPrdsByBOMWbID = {};
        ProductBOMService.dctBOMRowsByBOMWbID = {};
        ProductBOMService.dctComponentPrdsByBOMWbID = {};
        ProductBOMService.bIsLoaded = false;
    }

    private static instantiateDbContext() {
        ProductBOMService._dbContext = new PAdbContext();
    }

}